/**
 * PUBLIC PAST AUCTIONS ARCHIVE — browser-side copy of ../../archive-schema.mjs. Change both together.
 *
 *   archiveSales/{saleId}              ArchiveSale
 *   archiveSales/{saleId}/lots/{lotId} ArchiveLot
 *
 * A projection written by publish-archive.mjs (and, later, the HiBid/AuctionFlex migration). It carries
 * NO customer data: no buyer uid, bidder number, name, email, invoice number or invoice reference,
 * and no consignor. The archive pages read ONLY these two shapes — never auctions/{id}, whose lots
 * carry the winner's uid and bidder number.
 */
export type SystemOfRecord = 'auctionsaas' | 'hibid' | 'auctionflex' | 'liveauctioneers' | 'csv';

export const SYSTEM_LABEL: Record<SystemOfRecord, string> = {
  auctionsaas: 'AuctionSAAS',
  hibid: 'HiBid',
  auctionflex: 'AuctionFlex',
  liveauctioneers: 'LiveAuctioneers',
  csv: 'Imported',
};

export interface ArchiveSale {
  id: string;
  schemaVersion: number;
  title: string;
  startDate: unknown;          // Firestore Timestamp
  endDate: unknown;            // Firestore Timestamp
  venue: string | null;        // "City, ST"
  description: string | null;
  lotCount: number;
  lotsSold: number | null;     // null => no results data for this sale
  hasResults: boolean;
  systemOfRecord: SystemOfRecord;
  coverImage: string | null;
  publishedAt?: unknown;
}

export interface ArchiveLot {
  id: string;
  lotNumber: string;
  sortKey: number;
  title: string;
  description: string | null;
  images: string[];
  category: string | null;
  condition: string | null;
  result: 'sold' | 'unsold' | null;   // null => no results data
  realizedCents: number | null;       // hammer price, before buyer's premium
}

export const toDate = (v: any): Date | null => {
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate();
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** Sell-through as a whole-number percentage, or null when the sale has no results data. */
export const sellThrough = (s: Pick<ArchiveSale, 'hasResults' | 'lotsSold' | 'lotCount'>): number | null =>
  s.hasResults && s.lotsSold != null && s.lotCount > 0 ? Math.round((100 * s.lotsSold) / s.lotCount) : null;

export const formatRealized = (cents: number): string =>
  `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const mapLink = (venue: string): string =>
  `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(venue)}`;
